import { PaymentAdapter, TenantSchedule, TimeZone } from '@generated/tcea/models';
import { AggregatorConfig } from '@interfaces/aggregator/1.0';
import { ApiResponse, ApiResponseObject } from '@interfaces/api-response/1.0';
import { Decorator, DecoratorType } from '@interfaces/decorator/1.0';
import { POSProviderType } from '@interfaces/pos-integration/1.0';

export interface StoreInfoResponse {
  getStoresResult: {
    statusCode: number;
    stores: Store[];
  };
}

export interface StoreIntegrationProperty {
  name: string;
  value: any;
}

export interface Store extends ApiResponseObject {
  id: string;
  isPrimary: boolean;
  aggregatorIds: string[];
  aggregators?: AggregatorConfig[];
  tenantId: string;
  dataGroupId: string;
  country: string;
  timeZone?: {
    timeZoneCode: string;
    offsetUTC: string;
    offsetDST: string;
  };
  timeZoneCode?: string;
  name: string;
  state: string;
  city: string;
  enabled?: boolean;
  streetAddress: {
    address1: string;
    address2: string;
  };
  schedule: TenantSchedule[];
  franchise?: string;
  number: string;
  postalCode: string;
  phoneNumber?: string;
  queueName?: string;
  integrationType?: string;
  integrationVersion?: string;
  integrationProperties?: StoreIntegrationProperty[];
  modifierCodes?: any[];
  onboardingStatus?: string;
  prepTime?: string;
  posRespectsScheduling?: boolean;
  activationId?: string;
  isPublished?: string;
  isStatusOpen?: boolean;
  status?: {
    status?: StoreStatus;
  };
  posProvider?: POSProviderType;
  adapterDownloadUrl?: string;
  externalPosProviderId?: string;
  externalPosRegistrationId?: string;
  paymentAdapters?: string[];
  posIntegrationConfigurations?: {
    posProvider?: POSProviderType;
    hubParentStoreId?: string;
    externalPosProviderId?: string; // hubId in POS_SPI, queueName in RI
    externalPosRegistrationId?: string; // registrationId in POS_SPI, activationId in RI
    integrationType?: string;
    integrationVersion?: string;
    integrationProperties?: StoreIntegrationProperty[];
    priceOverride?: string;
    storeNumber?: string;
  };
  emails?: {
    outOfStock?: string;
  };
  coords?: {
    lat: number;
    lon: number;
  };
  taxRate: number;
  parent?: Parent;
  childBrands?: ChildBrands[];
  highSodiumThreshold?: {
    value: number;
    unit: string;
  };
  paymentAdapterList?: PaymentAdapter[];
  isStoreHoursSet?: boolean;
  externalStoreId?: string;
}

export interface StoreResponse extends ApiResponse {
  _embedded: {
    store: Store[];
  };
}

export interface StoreViewResponse extends ApiResponse {
  results: StoreView[];
}

export enum StoreStatus {
  ONLINE_AND_READY = 'onlineAndReady',
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export interface Parent {
  tenantId: string;
  storeId: string;
  tenantName?: string;
  storeName?: string;
}

export interface ChildBrands {
  tenantId: string;
  storeId: string;
  storeName: string;
  tenantName: string;
  editLink?: boolean;
}

export interface PauseOrderRow {
  aggregatorId: string;
  channel: string;
  channelStatus: string;
  excluded?: boolean;
  channelLabel: string;
  comments: string;
  statusDetails: string;
}

export enum ChannelStatus {
  ON = 'onlineAndReady',
  OFF = 'offline',
  PAUSED = 'online',
}

export interface QuantityLimit {
  catalogEntryId: string;
  name: string;
  limit: number;
  soldQty: number;
  state: { date: string; count: number };
  remainingQty: number;
}

export interface StoreView {
  id: string;
  tenantId: string;
  name: string;
  city: string;
  dataGroupId: string;
  enabled?: boolean;
  storeAddress: string;
  storeNumber: string;
  state: string;
  postalCode: string;
  storeStatus: string;
  isChannelSetup: boolean;
  isChildStore: boolean;
  isDefaultTaxRateSet: boolean;
  isOrderingEnabled: boolean;
  isPaymentConfigured: boolean;
  isPosConfigured: boolean;
  isPrepTimeSet: boolean;
  isPosOnlineAndReady: boolean;
  isStoreHoursSet: true;
  posStatus: string;
  posSystem: string;
  isExpanded?: boolean;
  isStatusOpen?: boolean;
  storeGroups?: any[];
}

export interface ImageItem {
  default?: boolean;
  groupIdentifier: string;
  images: {
    small?: {
      uploadUrl?: string;
      url?: string;
    };
    large?: {
      uploadUrl?: string;
      url?: string;
    };
  };
  translations: {};
}

export interface FormImageItem {
  default: boolean;
  images: {
    large?: {};
    small?: {};
  };
  decorators: Decorator[];
}

export interface TranslationKey {
  title?: string;
  description?: string;
  type: DecoratorType;
}

export enum DaysOfWeekAbbr {
  MONDAY = 'MON',
  TUESDAY = 'TUE',
  WEDNESDAY = 'WED',
  THURSDAY = 'THU',
  FRIDAY = 'FRI',
  SATURDAY = 'SAT',
  SUNDAY = 'SUN',
}

export enum DaysOfWeekLong {
  MON = 'MONDAY',
  TUE = 'TUESDAY',
  WED = 'WEDNESDAY',
  THU = 'THURSDAY',
  FRI = 'FRIDAY',
  SAT = 'SATURDAY',
  SUN = 'SUNDAY',
}

export interface NearbyStoresParams {
  latitude: number;
  longitude: number;
  radius?: number; // optional, default: 30
  includeDeliveryZones?: boolean; // optional, default: true
  enabledDeliveryZones?: boolean; // optional
  deliveryZoneType?: 'Primary' | 'Secondary'; // optional
  page?: number; // optional, default: 1
  size?: number; // optional, default: 25
}
